import { motion, useInView, Variants } from "framer-motion";
import { useRef } from "react";

interface AnimatedTextProps {
  text: string;
  className?: string;
  as?: "h1" | "h2" | "h3" | "h4" | "span" | "p";
  animation?: "chars" | "words" | "lines" | "blur-up";
  delay?: number;
  staggerDelay?: number;
  once?: boolean;
}

const AnimatedText = ({
  text,
  className = "",
  as: Component = "h2",
  animation = "words",
  delay = 0,
  staggerDelay = 0.03,
  once = true,
}: AnimatedTextProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once, amount: 0.5 });

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: delay,
      },
    },
  };

  const charVariants: Variants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      rotateX: -90,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  const wordVariants: Variants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      filter: "blur(8px)",
    },
    visible: { 
      opacity: 1, 
      y: 0,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 15,
      },
    },
  };

  const lineVariants: Variants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      skewY: 3,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      skewY: 0,
      transition: {
        type: "spring",
        stiffness: 60,
        damping: 15,
      },
    },
  };

  const blurUpVariants: Variants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      filter: "blur(12px)",
      scale: 0.95,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 20,
      },
    },
  };

  const getVariants = () => {
    switch (animation) {
      case "chars":
        return charVariants;
      case "words":
        return wordVariants;
      case "lines":
        return lineVariants;
      case "blur-up":
        return blurUpVariants;
      default:
        return wordVariants;
    }
  };

  const renderContent = () => {
    if (animation === "chars") {
      return text.split("").map((char, index) => (
        <motion.span
          key={index}
          variants={getVariants()}
          className="inline-block"
          style={{ 
            display: "inline-block",
            transformOrigin: "bottom center",
          }}
        >
          {char === " " ? "\u00A0" : char}
        </motion.span>
      ));
    }

    if (animation === "words") {
      return text.split(" ").map((word, index) => (
        <motion.span
          key={index}
          variants={getVariants()}
          className="inline-block mr-[0.25em]"
          style={{ display: "inline-block" }}
        >
          {word}
        </motion.span>
      ));
    }

    if (animation === "lines" || animation === "blur-up") {
      return (
        <motion.span
          variants={getVariants()}
          className="inline-block"
          style={{ display: "inline-block" }}
        >
          {text}
        </motion.span>
      );
    }

    return text;
  };

  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <Component className={className}>
          {renderContent()}
        </Component>
      </motion.div>
    </div>
  );
};

// Staggered section header component
interface SectionHeaderProps {
  badge?: string;
  title: string;
  subtitle?: string;
  className?: string;
  centered?: boolean;
}

export const SectionHeader = ({
  badge,
  title,
  subtitle,
  className = "",
  centered = true,
}: SectionHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <div ref={ref} className={`${centered ? "text-center" : ""} ${className}`}>
      {badge && (
        <motion.div
          className={`inline-flex items-center gap-3 mb-6 ${centered ? "" : ""}`}
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        >
          <motion.div 
            className="w-12 h-px bg-gradient-to-r from-transparent to-primary/60"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
            style={{ transformOrigin: "left" }}
          />
          <span className="pill">{badge}</span>
          <motion.div 
            className="w-12 h-px bg-gradient-to-l from-transparent to-primary/60"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
            style={{ transformOrigin: "right" }}
          />
        </motion.div>
      )}
      
      <AnimatedText
        text={title}
        as="h2"
        animation="words"
        delay={badge ? 0.3 : 0}
        staggerDelay={0.05}
        className="font-display text-5xl sm:text-6xl md:text-8xl text-foreground tracking-wide"
      />
      
      {subtitle && (
        <motion.p
          className="mt-6 text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.23, 1, 0.32, 1] }}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
};

export default AnimatedText;
